/* eslint-disable sonarjs/no-identical-functions */
// TODO remove eslint and fix error
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  EmployeeGeneralInformation,
  EmployeeGeneralInformationState,
  EmployeeMandatoryInformation,
  VendorGeneralInformation,
} from '../../../types/MyProfile/GeneralTab/generalInformationTypes'
import { RootState } from '../../../stateStore'
import { ValidationError } from '../../../types/commonTypes'
import employeeGeneralInformationApi from '../../../middleware/api/MyProfile/GeneralTab/generalInformationApi'

// Determine initial assignedCountry with workLocation precedence rules:
// If localStorage.workLocation === 'IND-AUS' (case-insensitive) use 'IND-AUS'
// If workLocation is null/undefined/empty -> ignore and fallback to employeeCountry
// Else fallback to employeeCountry (existing behavior)
const lsWorkLocation = (localStorage.getItem('workLocation') || '').trim()
const normalizedWorkLocation = lsWorkLocation.toLowerCase()
const lsEmployeeCountry = localStorage.getItem('employeeCountry')
const derivedAssignedCountry =
  normalizedWorkLocation === 'ind-aus'
    ? 'IND-AUS'
    : (lsEmployeeCountry as string | null)

const initialGeneralInformationState = {
  assignedCountry: derivedAssignedCountry,
  generalInformation: {} as EmployeeGeneralInformation,
  selectedEmployeeInformation: {} as EmployeeGeneralInformation,
  vendorInformation: {} as VendorGeneralInformation,
  mandatoryInformationFlags: {} as EmployeeMandatoryInformation,
  isLoading: false,
  error: null,
} as EmployeeGeneralInformationState

const getEmployeeGeneralInformation = createAsyncThunk<
  { generalInformation: EmployeeGeneralInformation } | undefined,
  string,
  { rejectValue: ValidationError }
>(
  'getLoggedInEmployeeData/getEmployeeGeneralInformation',
  async (employeeId: string, thunkApi) => {
    try {
      return await employeeGeneralInformationApi.getEmployeeGeneralInformation(
        employeeId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getVendorGeneralInformation = createAsyncThunk<
  { generalInformation: VendorGeneralInformation } | undefined,
  string,
  { rejectValue: ValidationError }
>(
  'getLoggedInEmployeeData/getVendorGeneralInformation',
  async (vendorId: string, thunkApi) => {
    try {
      return await employeeGeneralInformationApi.getVendorGeneralInformation(
        vendorId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getEmployeeBasicInfoDetails = createAsyncThunk<
  { generalInformation: EmployeeMandatoryInformation } | undefined,
  string,
  { rejectValue: ValidationError }
>(
  '/jobapplicant/getEmployeeBasicInfoDetails',
  async (employeeId: string, thunkApi) => {
    try {
      return await employeeGeneralInformationApi.getEmployeeBasicInfo(
        employeeId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

// same api but will be used to get the selected employee information on another state
// the other api url lacks the necessary data
// keep in mind this change will lessen the security of data - the other users can now see all the data of the user with correct priveleges.
const getSelectedEmployeeInformation = createAsyncThunk<
  { generalInformation: EmployeeGeneralInformation } | undefined,
  string,
  { rejectValue: ValidationError }
>(
  'getLoggedInEmployeeData/getSelectedEmployeeInformation',
  async (employeeId: string, thunkApi) => {
    try {
      //* revert this back if you want the data to be more secure and tell the backend to bind the necessary data to the getOtherEmployeeGeneralInformation api
      // return await employeeGeneralInformationApi.getOtherEmployeeGeneralInformation(
      return await employeeGeneralInformationApi.getEmployeeGeneralInformation(
        employeeId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const employeeGeneralInformationSlice = createSlice({
  name: 'getLoggedInEmployeeData',
  initialState: initialGeneralInformationState,
  reducers: {
    setEmployeeGeneralInformation: (state, action) => {
      return { ...state, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeGeneralInformation.fulfilled, (state, action) => {
        const payload = action.payload as unknown as EmployeeGeneralInformation
        state.generalInformation = payload
        // state.selectedEmployeeInformation =
        //   action.payload as unknown as EmployeeGeneralInformation
        // Apply workLocation precedence rule on runtime fetch as well
        const runtimeWorkLocation = (
          payload as unknown as { workLocation?: string | null }
        )?.workLocation
        if (
          typeof runtimeWorkLocation === 'string' &&
          runtimeWorkLocation.toLowerCase() === 'ind-aus'
        ) {
          state.assignedCountry = 'IND-AUS'
          // Optionally persist for later reloads
          try {
            localStorage.setItem('workLocation', 'IND-AUS')
          } catch (e) {
            /* ignore storage errors */
          }
        } else {
          state.assignedCountry = payload.country as string
        }
        state.isLoading = false
      })

      .addCase(getSelectedEmployeeInformation.fulfilled, (state, action) => {
        const payload = action.payload as unknown as EmployeeGeneralInformation
        state.selectedEmployeeInformation = payload
        state.isLoading = false
      })
      .addCase(getVendorGeneralInformation.fulfilled, (state, action) => {
        const payload = action.payload as unknown as VendorGeneralInformation
        state.vendorInformation = payload
        state.isLoading = false
      })
      .addCase(getEmployeeBasicInfoDetails.fulfilled, (state, action) => {
        const payload =
          action.payload as unknown as EmployeeMandatoryInformation
        state.mandatoryInformationFlags = payload
        state.isLoading = false
      })
      .addMatcher(
        isAnyOf(
          getEmployeeGeneralInformation.pending,
          getSelectedEmployeeInformation.pending,
          getVendorGeneralInformation.pending,
          getEmployeeBasicInfoDetails.pending,
        ),
        (state) => {
          state.isLoading = true
        },
      )
      .addMatcher(
        isAnyOf(
          getEmployeeGeneralInformation.rejected,
          getSelectedEmployeeInformation.rejected,
          getVendorGeneralInformation.rejected,
          getEmployeeBasicInfoDetails.rejected,
        ),
        (state, action) => {
          state.isLoading = false
          state.error = action.payload as ValidationError
        },
      )
  },
})
export const { setEmployeeGeneralInformation, clearError } =
  employeeGeneralInformationSlice.actions

const selectLoggedInEmployeeData = (
  state: RootState,
  isViewingAnotherEmployee = false,
): EmployeeGeneralInformation =>
  isViewingAnotherEmployee
    ? state.getLoggedInEmployeeData.selectedEmployeeInformation
    : state.getLoggedInEmployeeData.generalInformation

const generalInformation = (state: RootState): EmployeeGeneralInformation =>
  state.getLoggedInEmployeeData.generalInformation

const empMandatoryFlags = (state: RootState): EmployeeMandatoryInformation =>
  state.getLoggedInEmployeeData.mandatoryInformationFlags

const isLoading = (state: RootState): boolean =>
  state?.getLoggedInEmployeeData.isLoading

const getAssignedCountry = (state: RootState): string =>
  state?.getLoggedInEmployeeData.assignedCountry

export const getEmployeeGeneralInformationThunk = {
  getEmployeeGeneralInformation,
  getSelectedEmployeeInformation,
  getVendorGeneralInformation,
  getEmployeeBasicInfoDetails,
}

export const loggedInEmployeeSelectors = {
  selectLoggedInEmployeeData,
  generalInformation,
  isLoading,
  getAssignedCountry,
  empMandatoryFlags,
}
export const generalInformationService = {
  ...getEmployeeGeneralInformationThunk,
  actions: employeeGeneralInformationSlice.actions,
  selectors: loggedInEmployeeSelectors,
}

export default employeeGeneralInformationSlice.reducer
